import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Divider,
    GlobalStyles,
    IconButton,
    InputAdornment,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import Screen, { type ScreenProps } from 'ui/layouts/Screen';
import { HEADER } from 'config';
import {
    directApiEndpoints,
    errorMessages,
    processorEndpoints,
    whitelistEndpoints,
    type Endpoint,
    type Parameter
} from './content';
import TransactionPlayground from './TransactionPlayground';
import usePotentialPaymentMethods from './usePotentialPaymentMethods';

const MethodBadge = ({ method }: Pick<Endpoint, 'method'>) => (
    <Chip
        size='small'
        label={method}
        color={method === 'GET' ? 'success' : 'primary'}
        sx={{ fontWeight: 700, minWidth: 58 }}
    />
);

const CodeBlock = ({ title, children }: { readonly title: string; readonly children: string }) => {
    const [copied, setCopied] = useState(false);

    const copy = () => {
        navigator.clipboard.writeText(children)
            .then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
            });
    };

    return (
        <Paper
            variant='outlined'
            sx={{ overflow: 'hidden', bgcolor: '#101828', color: 'common.white' }}
        >
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ px: 2, py: 1, bgcolor: '#1d2939' }}
            >
                <Typography variant='caption' sx={{ color: '#d0d5dd' }}>
                    {title}
                </Typography>
                <IconButton size='small' onClick={copy} sx={{ color: '#d0d5dd' }}>
                    <ContentCopyRounded fontSize='small' />
                </IconButton>
            </Stack>
            <Box
                component='pre'
                sx={{
                    m: 0,
                    p: 2.5,
                    overflow: 'auto',
                    fontSize: 13,
                    lineHeight: 1.7,
                    fontFamily: 'monospace'
                }}
            >
                {children}
            </Box>
            {copied && (
                <Typography variant='caption' sx={{ display: 'block', px: 2, pb: 1, color: '#7ee787' }}>
                    Copied
                </Typography>
            )}
        </Paper>
    );
};

const ParametersTable = ({ parameters }: { readonly parameters: readonly Parameter[] }) => (
    <TableContainer component={Paper} variant='outlined'>
        <Table size='small'>
            <TableHead>
                <TableRow>
                    <TableCell>Parameter</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Description</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {parameters.map(parameter => (
                    <TableRow key={parameter.name}>
                        <TableCell>
                            <Box component='code'>{parameter.name}</Box>
                        </TableCell>
                        <TableCell>{parameter.type}</TableCell>
                        <TableCell>{parameter.required ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{parameter.description}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const EndpointSection = ({ endpoint, compact = false }: {
    readonly endpoint: Endpoint;
    readonly compact?: boolean;
}) => (
    <Box
        component='section'
        id={endpoint.id}
        sx={{ scrollMarginTop: 88, py: compact ? 2 : 4 }}
    >
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 1.5 }}>
            <MethodBadge method={endpoint.method} />
            <Typography variant={compact ? 'h6' : 'h4'}>{endpoint.title}</Typography>
        </Stack>
        <Box
            component='code'
            sx={{
                display: 'inline-block',
                px: 1.5,
                py: 0.75,
                mb: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                color: 'text.primary'
            }}
        >
            {endpoint.path}
        </Box>
        <Typography color='text.secondary' sx={{ mb: endpoint.parameters ? 3 : 0 }}>
            {endpoint.description}
        </Typography>
        {endpoint.note && <Alert severity='info' sx={{ my: 2 }}>{endpoint.note}</Alert>}
        {endpoint.parameters && <ParametersTable parameters={endpoint.parameters} />}
        {!compact && endpoint.request && (
            <Box sx={{ mt: 3 }}>
                <CodeBlock title='Request'>{endpoint.request}</CodeBlock>
            </Box>
        )}
        {!compact && endpoint.response && (
            <Box sx={{ mt: 2 }}>
                <CodeBlock title='Response'>{endpoint.response}</CodeBlock>
            </Box>
        )}
        {!compact && endpoint.responseExamples?.map(example => (
            <Box key={example.title} sx={{ mt: 2 }}>
                <CodeBlock title={example.title}>{example.content}</CodeBlock>
            </Box>
        ))}
    </Box>
);

const navigation = [
    {
        title: 'Getting started',
        links: [
            ['overview', 'Overview'],
            ['environments', 'Environments'],
            ['authentication', 'Authentication'],
            ['transaction-flow', 'Transaction flow']
        ]
    },
    {
        title: 'Payment Processor',
        links: processorEndpoints.map(endpoint => [endpoint.id, endpoint.title])
    },
    {
        title: 'Integration',
        links: [
            ['go-live-testing', 'Before going live'],
            ['notifications', 'Notifications'],
            ['webhook', 'Webhook redirect'],
            ['live-payment-methods', 'Payment methods'],
            ['statuses', 'Statuses'],
            ['errors', 'Error messages']
        ]
    },
    {
        title: 'Whitelist imports',
        links: [
            ['whitelist-process', 'Whitelist process'],
            ['whitelist-environments', 'Environments'],
            ['hashed-email-whitelist', 'Hashed email whitelist'],
            ...whitelistEndpoints.map(endpoint => [endpoint.id, endpoint.title])
        ]
    },
    {
        title: 'Reporting and Payout API',
        links: directApiEndpoints.map(endpoint => [endpoint.id, endpoint.title])
    }
] as const;

const getEndpointSearchContent = (endpoint: Endpoint) => [
    endpoint.id,
    endpoint.title,
    endpoint.method,
    endpoint.path,
    endpoint.description,
    endpoint.note,
    endpoint.request,
    endpoint.response,
    endpoint.parameters?.flatMap(parameter => [
        parameter.name,
        parameter.type,
        parameter.description,
        parameter.required ? 'required yes' : 'required no optional'
    ]),
    endpoint.responseExamples?.flatMap(example => [example.title, example.content])
].flat(Infinity).filter(Boolean).join(' ');

const Documentation = (props: ScreenProps) => {
    const [query, setQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'documentation' | 'playground'>('documentation');
    const potentialPaymentMethods = usePotentialPaymentMethods();
    const isProduction = window.location.hostname.toLowerCase() === 'merchants.macropay.net';
    const normalizedQuery = query.trim().toLowerCase();

    const matches = (value: string) => !normalizedQuery || value.toLowerCase().includes(normalizedQuery);
    const filteredProcessor = processorEndpoints.filter(endpoint =>
        matches(getEndpointSearchContent(endpoint))
    );
    const filteredWhitelist = whitelistEndpoints.filter(endpoint =>
        matches(getEndpointSearchContent(endpoint))
    );
    const filteredDirect = directApiEndpoints.filter(endpoint =>
        matches(getEndpointSearchContent(endpoint))
    );
    const printDocumentation = () => {
        setQuery('');
        window.setTimeout(() => window.print(), 0);
    };

    return (
        <Screen {...props} title='API Documentation'>
            <GlobalStyles styles={{
                '@media print': {
                    'body *': { visibility: 'hidden' },
                    '.documentation-print-root, .documentation-print-root *': { visibility: 'visible' },
                    '.documentation-print-root': {
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        background: '#fff'
                    },
                    '.documentation-print-hidden': { display: 'none !important' },
                    '.documentation-print-layout': { display: 'block !important' },
                    '.documentation-print-content': {
                        margin: '0 !important',
                        padding: '20px !important',
                        boxShadow: 'none !important'
                    },
                    '.documentation-print-content section, .documentation-print-content table, .documentation-print-content pre': {
                        breakInside: 'avoid'
                    }
                }
            }} />
            <Box className='documentation-print-root' sx={{ bgcolor: 'background.neutral', minHeight: '100vh' }}>
                <Box
                    className='documentation-print-hidden'
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Container maxWidth='xl'>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent='space-between'
                            spacing={2}
                            sx={{ py: 2 }}
                        >
                            <Box>
                                <Typography variant='h5'>Macropay Documentation</Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <TextField
                                    sx={{
                                        display: activeTab === 'documentation' ? 'flex' : 'none',
                                        width: { xs: 1, md: 360 }
                                    }}
                                    size='small'
                                    value={query}
                                    onChange={event => setQuery(event.target.value)}
                                    placeholder='Search documentation'
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <SearchRounded fontSize='small' />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                {activeTab === 'documentation' && (
                                    <Button variant='outlined' startIcon={<PrintRounded />} onClick={printDocumentation}>
                                        Save as PDF
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                        <Tabs
                            value={activeTab}
                            onChange={(_, value: 'documentation' | 'playground') => setActiveTab(value)}
                            aria-label='Documentation sections'
                        >
                            <Tab value='documentation' label='Documentation' />
                            {!isProduction && <Tab value='playground' label='API Playground' />}
                        </Tabs>
                    </Container>
                </Box>

                {activeTab === 'documentation' ? (
                <Container maxWidth='xl' disableGutters sx={{ px: { xs: 1, lg: 1.5 } }}>
                    <Box
                        className='documentation-print-layout'
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', lg: '260px minmax(0, 1fr)' },
                            gap: { xs: 3, lg: 6 },
                            alignItems: 'start',
                            position: 'relative'
                        }}
                    >
                        <Box
                            component='nav'
                            className='documentation-print-hidden'
                            sx={{
                                display: { xs: 'none', lg: 'block' },
                                position: 'sticky',
                                top: `${HEADER.H_DASHBOARD_DESKTOP + 12}px`,
                                alignSelf: 'start',
                                zIndex: 2,
                                py: 2,
                                maxHeight: `calc(100vh - ${HEADER.H_DASHBOARD_DESKTOP + 24}px)`,
                                overflowY: 'auto',
                                overscrollBehavior: 'contain',
                                bgcolor: 'background.neutral'
                            }}
                        >
                            {navigation.map(group => (
                                <Box
                                    key={group.title}
                                    sx={group.title === 'Whitelist imports' ? {
                                        mb: 3,
                                        p: 1.5,
                                        border: 1,
                                        borderColor: 'primary.main',
                                        borderRadius: 1.5,
                                        bgcolor: 'primary.lighter'
                                    } : { mb: 3 }}
                                >
                                    <Link
                                        href={`#${group.links[0][0]}`}
                                        underline='none'
                                        variant='overline'
                                        color={group.title === 'Whitelist imports' ? 'primary.main' : 'text.secondary'}
                                        sx={{
                                            display: 'block',
                                            px: 0.5,
                                            fontWeight: 700,
                                            borderRadius: 1,
                                            '&:hover': { color: 'text.primary' }
                                        }}
                                    >
                                        {group.title}
                                    </Link>
                                    <Stack sx={{ mt: 0.5 }}>
                                        {group.links.map(([id, label]) => (
                                            <Link
                                                key={id}
                                                href={`#${id}`}
                                                underline='none'
                                                color='text.secondary'
                                                sx={{
                                                    px: 0.5,
                                                    py: 0.55,
                                                    borderRadius: 1,
                                                    fontSize: 14,
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                        color: 'text.primary'
                                                    }
                                                }}
                                            >
                                                {label}
                                            </Link>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Box>

                        <Paper
                            className='documentation-print-content'
                            elevation={0}
                            sx={{
                                my: { xs: 2, lg: 4 },
                                px: { xs: 2, md: 5 },
                                py: { xs: 2, md: 4 },
                                minWidth: 0
                            }}
                        >
                            {!normalizedQuery && (
                                <>
                                    <Box component='section' id='overview' sx={{ scrollMarginTop: 88, pb: 4 }}>
                                        <Typography variant='h3' sx={{ mb: 2 }}>
                                            Integrate with Macropay
                                        </Typography>
                                        <Typography variant='h6' color='text.secondary' sx={{ maxWidth: 760 }}>
                                            Use the Macropay HTTP APIs to accept payments, follow transaction
                                            statuses, receive notifications and access merchant reporting data.
                                        </Typography>
                                    </Box>

                                    <Divider />

                                    <Box component='section' id='environments' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Environments</Typography>
                                        <Typography color='text.secondary' paragraph>
                                            All API communication uses HTTPS and UTF-8. TLS 1.2 or later is required.
                                        </Typography>
                                        <Stack spacing={1}>
                                            <CodeBlock title='Test base URL'>https://testapi.payments-gateway.co/v1</CodeBlock>
                                            <CodeBlock title='Live base URL'>https://api.payments-gateway.co/v1</CodeBlock>
                                        </Stack>
                                    </Box>

                                    <Divider />

                                    <Box component='section' id='authentication' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Authentication</Typography>
                                        <Typography color='text.secondary' paragraph>
                                            Your API ID and API GUID are available in Profile → Technical Information.
                                            Keep them secret and use the credentials that belong to the selected environment.
                                        </Typography>
                                        <Stack spacing={1.5} sx={{ mb: 2 }}>
                                            <Alert severity='info'>
                                                <strong>Transaction and transaction status:</strong> send
                                                <Box component='code' sx={{ mx: 0.5 }}>api_id</Box> and
                                                <Box component='code' sx={{ mx: 0.5 }}>api_guid</Box> in the request body,
                                                or send them as request headers
                                                <Box component='code' sx={{ mx: 0.5 }}>api_id: &lt;API ID&gt;</Box> and
                                                <Box component='code' sx={{ mx: 0.5 }}>api_secret: &lt;API GUID&gt;</Box>.
                                                Send <Box component='code'>Accept: application/json</Box> in the request
                                                headers. The body supports
                                                <Box component='code' sx={{ mx: 0.5 }}>Content-Type: application/json</Box>
                                                or
                                                <Box component='code' sx={{ ml: 0.5 }}>Content-Type: application/x-www-form-urlencoded</Box>.
                                            </Alert>
                                            <Alert severity='info'>
                                                <strong>Payment methods and consumer endpoints:</strong> send the following
                                                request headers: <Box component='code' sx={{ mx: 0.5 }}>auth-id: &lt;API ID&gt;</Box>,
                                                <Box component='code' sx={{ mx: 0.5 }}>auth-secret: &lt;API GUID&gt;</Box> and
                                                <Box component='code' sx={{ mx: 0.5 }}>Accept: application/json</Box>.
                                                For JSON request bodies use
                                                <Box component='code' sx={{ mx: 0.5 }}>Content-Type: application/json</Box>;
                                                for Consumer whitelist and KYC use
                                                <Box component='code' sx={{ ml: 0.5 }}>Content-Type: multipart/form-data</Box>.
                                            </Alert>
                                        </Stack>
                                        <Alert severity='warning'>
                                            Never expose API credentials in public client-side code or commit them to source control.
                                        </Alert>
                                    </Box>

                                    <Divider />

                                    <Box component='section' id='transaction-flow' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Transaction flow</Typography>
                                        <Stack spacing={1.25} sx={{ mt: 2 }}>
                                            {[
                                                'Your checkout creates a transaction.',
                                                'Macropay selects the payment method using currency, country, channel, tag and optional specin fields.',
                                                'Redirect the consumer to the one-time URL from the response.',
                                                'The consumer completes the payment with the issuer.',
                                                'Macropay sends status notifications to your notification URL.',
                                                'Confirm the latest state with getTransactionStatus.'
                                            ].map((step, index) => (
                                                <Stack key={step} direction='row' spacing={2} alignItems='center'>
                                                    <Chip label={index + 1} size='small' color='primary' />
                                                    <Typography>{step}</Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>
                                </>
                            )}

                            {filteredProcessor.map((endpoint, index) => (
                                <Box key={endpoint.id}>
                                    <Divider />
                                    <EndpointSection endpoint={endpoint} />
                                </Box>
                            ))}

                            {!normalizedQuery && (
                                <>
                                    <Divider />
                                    <Box
                                        component='section'
                                        id='go-live-testing'
                                        sx={{
                                            scrollMarginTop: 88,
                                            my: 4,
                                            p: { xs: 2.5, md: 3 },
                                            border: 1,
                                            borderColor: 'warning.main',
                                            borderRadius: 2,
                                            bgcolor: 'warning.lighter'
                                        }}
                                    >
                                        <Typography variant='h4' gutterBottom>
                                            Before going live
                                        </Typography>
                                        <Alert severity='warning' sx={{ mb: 3 }}>
                                            When testing payment methods in production, use only generic test email
                                            addresses such as <Box component='code'>testswish@test.com</Box> or
                                            generic Yopmail test addresses. Do not use email addresses containing
                                            Macropay, merchant, brand or other entity domain names.
                                        </Alert>
                                        <Typography color='text.secondary' paragraph>
                                            Because production methods are being tested, Macropay needs an end-to-end
                                            recording before go-live sign-off. The recording should cover one brand
                                            that will be pushed live, from cashier through the payment method flow and
                                            redirect back to the merchant site. Ideally, record a successful end-to-end
                                            payment.
                                        </Typography>
                                        <Typography color='text.secondary'>
                                            Send the recording to Macropay by email for review.
                                        </Typography>
                                    </Box>

                                    <Divider />
                                    <Box component='section' id='notifications' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Notifications</Typography>
                                        <Typography color='text.secondary' paragraph>
                                            Notifications are mandatory. Macropay sends an
                                            application/x-www-form-urlencoded POST whenever a transaction state changes.
                                            Return a 2xx response within 30 seconds after processing the notification.
                                        </Typography>
                                        <Alert severity='info'>
                                            Transaction states may change after an earlier FAILED or SUCCEEDED state.
                                            Always design notification processing to be idempotent.
                                        </Alert>
                                    </Box>

                                    <Divider />
                                    <Box component='section' id='webhook' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Webhook redirect</Typography>
                                        <Typography color='text.secondary'>
                                            After authorization, the consumer may be redirected to the merchant redirect URL.
                                            Do not rely on this browser redirect to finalize an order. Use notifications and
                                            getTransactionStatus as the authoritative sources.
                                        </Typography>
                                    </Box>

                                    <Divider />
                                    <Box component='section' id='live-payment-methods' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Available payment methods</Typography>
                                        <Typography color='text.secondary' paragraph>
                                            Explore the payment methods available through Macropay. Each entry shows
                                            the exact integration tag together with the supported countries and currencies.
                                        </Typography>
                                        {potentialPaymentMethods.isLoading && (
                                            <Stack direction='row' spacing={1.5} alignItems='center' sx={{ py: 2 }}>
                                                <CircularProgress size={22} />
                                                <Typography color='text.secondary'>Loading payment methods…</Typography>
                                            </Stack>
                                        )}
                                        {potentialPaymentMethods.error && (
                                            <Alert severity='error' sx={{ mb: 2 }}>
                                                {potentialPaymentMethods.error}
                                            </Alert>
                                        )}
                                        {!potentialPaymentMethods.isLoading &&
                                            !potentialPaymentMethods.error &&
                                            !potentialPaymentMethods.methods.length && (
                                                <Alert severity='info' sx={{ mb: 2 }}>
                                                    No payment methods are currently available.
                                                </Alert>
                                            )}
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                                gap: 2
                                            }}
                                        >
                                            {potentialPaymentMethods.methods.map(method => (
                                                <Paper key={method.tag} variant='outlined' sx={{ p: 2.5 }}>
                                                    <Typography variant='h6'>{method.name}</Typography>
                                                    <Typography variant='caption' color='text.secondary'>Tag</Typography>
                                                    <Typography component='div' sx={{ mb: 2 }}>
                                                        <Box component='code'>{method.tag}</Box>
                                                    </Typography>
                                                    <Typography variant='caption' color='text.secondary'>Countries</Typography>
                                                    <Stack direction='row' gap={0.75} flexWrap='wrap' sx={{ mt: 0.5, mb: 1.5 }}>
                                                        {method.countries.map(value => (
                                                            <Chip key={value} label={value} size='small' />
                                                        ))}
                                                    </Stack>
                                                    <Typography variant='caption' color='text.secondary'>Currencies</Typography>
                                                    <Stack direction='row' gap={0.75} flexWrap='wrap' sx={{ mt: 0.5 }}>
                                                        {method.currencies.map(value => (
                                                            <Chip key={value} label={value} size='small' variant='outlined' />
                                                        ))}
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </Box>
                                    </Box>

                                    <Divider />
                                    <Box component='section' id='statuses' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Statuses and funds statuses</Typography>
                                        <Typography color='text.secondary' paragraph>
                                            Treat notifications as state changes rather than a single immutable final result.
                                        </Typography>
                                        <Stack direction='row' gap={1} flexWrap='wrap'>
                                            {['PENDING', 'REJECT', 'FAILED', 'SUCCEEDED', 'CHARGE_BACK', 'REFUNDED'].map(status => (
                                                <Chip key={status} label={status} />
                                            ))}
                                        </Stack>
                                        <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>Funds statuses</Typography>
                                        <Stack direction='row' gap={1} flexWrap='wrap'>
                                            {['NOT_EXPECTED', 'WAITING', 'MISSING', 'RECEIVED', 'SENT', 'REFUNDED'].map(status => (
                                                <Chip key={status} label={status} variant='outlined' />
                                            ))}
                                        </Stack>
                                        <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>Settlement statuses</Typography>
                                        <Stack direction='row' gap={1} flexWrap='wrap'>
                                            {['SUCCEEDED', 'FUNDSMISSING', 'FUNDSRECEIVED', 'FUNDSREFUNDED', 'FUNDSSENT'].map(status => (
                                                <Chip key={status} label={status} color='secondary' variant='outlined' />
                                            ))}
                                        </Stack>
                                    </Box>

                                    <Divider />
                                    <Box component='section' id='errors' sx={{ scrollMarginTop: 88, py: 4 }}>
                                        <Typography variant='h4' gutterBottom>Error messages</Typography>
                                        <TableContainer component={Paper} variant='outlined' sx={{ mt: 2 }}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Message</TableCell>
                                                        <TableCell>Description</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {errorMessages.map(([message, description]) => (
                                                        <TableRow key={message}>
                                                            <TableCell>{message}</TableCell>
                                                            <TableCell>{description}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Box>
                                </>
                            )}

                            {!!filteredWhitelist.length && (
                                <>
                                    <Divider />
                                    <Typography variant='h3' sx={{ pt: 4 }}>Whitelist imports</Typography>
                                    <Typography color='text.secondary' sx={{ mt: 1 }}>
                                        Import, inspect and download consumer whitelist data.
                                    </Typography>
                                    {!normalizedQuery && (
                                        <Box
                                            component='section'
                                            id='whitelist-process'
                                            sx={{
                                                scrollMarginTop: 88,
                                                my: 3,
                                                p: { xs: 2.5, md: 3 },
                                                border: 2,
                                                borderColor: 'primary.main',
                                                borderRadius: 2,
                                                bgcolor: 'primary.lighter'
                                            }}
                                        >
                                            <Alert severity='warning' sx={{ mb: 3 }}>
                                                <Typography variant='subtitle2'>Production only</Typography>
                                                The whitelist process applies when your integration moves to the
                                                Production environment. Prepare the flow in advance to avoid declined
                                                deposits when going live.
                                            </Alert>

                                            <Typography variant='h4' gutterBottom>Whitelist process</Typography>
                                            <Typography color='text.secondary' paragraph>
                                                Consumer whitelists can be submitted manually through the Macropay
                                                Backoffice or automatically through the API endpoints documented below.
                                                API automation is recommended for recurring consumer lists because it
                                                removes the need for repeated manual uploads.
                                            </Typography>

                                            <Alert severity='info' sx={{ my: 3 }}>
                                                <Typography variant='subtitle2' gutterBottom>
                                                    Whitelisting criteria
                                                </Typography>
                                                By default, unless otherwise agreed in the commercial terms, a consumer
                                                is eligible for whitelisting after completing at least four successful
                                                deposits using payment methods not provided by Macropay.
                                            </Alert>

                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, my: 3 }}>
                                                <Paper variant='outlined' sx={{ p: 2.5 }}>
                                                    <Typography variant='h6' gutterBottom>Backoffice import</Typography>
                                                    <Typography color='text.secondary'>
                                                        Upload the whitelist file directly from the Merchant Backoffice
                                                        when a manual submission is appropriate.
                                                    </Typography>
                                                </Paper>
                                                <Paper variant='outlined' sx={{ p: 2.5 }}>
                                                    <Typography variant='h6' gutterBottom>API automation</Typography>
                                                    <Typography color='text.secondary'>
                                                        Configure your integration to submit recurring consumer lists
                                                        directly to Macropay, without manually uploading each file.
                                                    </Typography>
                                                </Paper>
                                            </Box>

                                            <Alert severity='info' sx={{ mb: 3 }}>
                                                All received whitelist files are processed at the beginning of every
                                                full hour (HH:00). For example, a file received at 16:15 is processed
                                                at 17:00. Consumers included in that file can deposit after processing
                                                completes.
                                            </Alert>

                                            <Typography variant='h6' gutterBottom>
                                                Why the whitelist must be submitted in advance
                                            </Typography>
                                            <Typography color='text.secondary' paragraph>
                                                If a consumer attempts to deposit before being whitelisted, the
                                                transaction is declined with the response <Box component='code'>
                                                    User is not in ecosystem
                                                </Box>. Retrying does not change the result. The same response is
                                                returned until the consumer is included in a whitelist and the file
                                                has been successfully processed.
                                            </Typography>
                                            <Typography color='text.secondary' paragraph>
                                                Once processing is complete, the consumer should be able to complete
                                                the deposit on the next attempt. Submit whitelist files early enough
                                                to account for the next hourly processing window.
                                            </Typography>
                                            <Alert severity='warning' sx={{ mt: 3 }}>
                                                The whitelist template column order must be kept exactly as provided by
                                                Macropay. Headers are mandatory because the first CSV row is treated as
                                                the header row and skipped during import. Optional columns must remain in
                                                the file, but their values can be left empty. If the template column order
                                                is not respected, the data is considered invalid.
                                            </Alert>
                                            <Typography color='text.secondary' sx={{ mt: 3 }} paragraph>
                                                After a whitelist file is uploaded through the API, it can be reviewed in
                                                the Backoffice under Consumers → Whitelist Import History.
                                            </Typography>
                                            <Stack spacing={1}>
                                                <Typography color='text.secondary'>
                                                    <Box component='strong'>Imported Consumers</Box> is the total number
                                                    of new consumers imported.
                                                </Typography>
                                                <Typography color='text.secondary'>
                                                    <Box component='strong'>Updated Consumers</Box> is the number of
                                                    consumers that already existed in the system and were updated with
                                                    the details sent through the whitelist file.
                                                </Typography>
                                                <Typography color='text.secondary'>
                                                    <Box component='strong'>Ignored Consumers</Box> is the number of
                                                    consumers that were not imported because their data was broken or
                                                    invalid.
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                    {!normalizedQuery && (
                                        <Box
                                            component='section'
                                            id='whitelist-environments'
                                            sx={{ scrollMarginTop: 88, pt: 3, pb: 1 }}
                                        >
                                            <Typography variant='h4' gutterBottom>Environments</Typography>
                                            <Typography color='text.secondary' paragraph>
                                                Import or upload a CSV file with whitelisted consumers via API.
                                            </Typography>
                                            <Stack spacing={1}>
                                                <CodeBlock title='Sandbox URL'>
                                                    https://testapi.bo.payments-gateway.co
                                                </CodeBlock>
                                                <CodeBlock title='Production URL'>
                                                    https://api.bo.payments-gateway.co
                                                </CodeBlock>
                                            </Stack>
                                        </Box>
                                    )}
                                    <Alert severity='info' sx={{ mt: 2, mb: 2 }}>
                                        Every whitelist API request must include
                                        <Box component='code' sx={{ mx: 0.5 }}>auth-id</Box> (API ID) and
                                        <Box component='code' sx={{ mx: 0.5 }}>auth-secret</Box> (API GUID).
                                        Use <Box component='code'>Accept: application/json</Box>, or
                                        <Box component='code' sx={{ ml: 0.5 }}>Accept: text/csv</Box> when downloading
                                        a CSV stream. File uploads use
                                        <Box component='code' sx={{ ml: 0.5 }}>Content-Type: multipart/form-data</Box>.
                                    </Alert>
                                    {!normalizedQuery && (
                                        <Box
                                            component='section'
                                            id='hashed-email-whitelist'
                                            sx={{ scrollMarginTop: 88, pb: 4 }}
                                        >
                                            <Typography variant='h4' gutterBottom>
                                                Hashed email whitelist
                                            </Typography>
                                            <Typography color='text.secondary' paragraph>
                                                If your integration must avoid sending plain-text email addresses in
                                                whitelist files, hash each email before creating the CSV. Hashing is a
                                                one-way operation and is not encryption; Macropay does not provide a
                                                service that hashes or encrypts whitelist data on your behalf.
                                            </Typography>

                                            <Alert severity='warning' sx={{ mb: 3 }}>
                                                Request the whitelist email hashing secret from Macropay. This is a
                                                dedicated secret and is not your API GUID, <Box component='code'>auth-secret</Box>,
                                                or another merchant API credential. Keep it server-side and do not expose
                                                it in browser code, CSV files or source control.
                                            </Alert>

                                            <Typography variant='h6' gutterBottom>Hashing algorithm</Typography>
                                            <Typography color='text.secondary' paragraph>
                                                Convert the email to lowercase, append the Macropay-provided
                                                <Box component='code' sx={{ mx: 0.5 }}>EMAILS_HASH_SECRET</Box>, calculate
                                                SHA-256, and then calculate SHA-256 again over the first hexadecimal hash.
                                                Use the same logic in your server-side language if your integration is not
                                                written in PHP.
                                            </Typography>
                                            <CodeBlock title='PHP example'>{`function getHashedEmail(string $email, string $emailsHashSecret): string
{
    return hash(
        'sha256',
        hash('sha256', strtolower($email) . $emailsHashSecret)
    );
}`}</CodeBlock>

                                            <Typography variant='h6' sx={{ mt: 3 }} gutterBottom>CSV format</Typography>
                                            <Typography color='text.secondary' paragraph>
                                                Use the columns from the downloaded whitelist template. Phone is mandatory
                                                and must be provided as the fourth CSV column. The optional
                                                <Box component='code' sx={{ mx: 0.5 }}>transaction_count</Box> column can
                                                be included in both the simplified and EDD whitelist formats. Optional
                                                columns must still remain in the CSV file in the template order, but their
                                                values can be left empty.
                                            </Typography>
                                            <CodeBlock title='CSV example'>{`"First name","Last name","Email or hashed email","Phone","transaction_count"
"","","<hashed-email>","<phone>","12"`}</CodeBlock>
                                            <Alert severity='info' sx={{ mt: 3 }}>
                                                Hash the exact email value consistently. The same lowercase email and the
                                                same Macropay-provided secret must always produce the same hash so that the
                                                consumer can be matched during transaction processing.
                                            </Alert>
                                        </Box>
                                    )}
                                    {filteredWhitelist.map(endpoint => (
                                        <EndpointSection key={endpoint.id} endpoint={endpoint} />
                                    ))}
                                </>
                            )}

                            {!!filteredDirect.length && (
                                <>
                                    <Divider />
                                    <Typography variant='h3' sx={{ pt: 4 }}>Reporting and Payout API</Typography>
                                    <Typography color='text.secondary' sx={{ mt: 1, mb: 2 }}>
                                        Authenticated reporting, payout and export operations documented in the
                                        Reporting and Payout API section.
                                    </Typography>
                                    <Alert severity='info' sx={{ mb: 2 }}>
                                        Every request must include <Box component='code'>auth-id</Box> (API ID) and
                                        <Box component='code' sx={{ ml: 0.5 }}>auth-secret</Box> (API GUID).
                                        Use <Box component='code' sx={{ ml: 0.5 }}>Accept: application/json</Box>, or
                                        <Box component='code' sx={{ ml: 0.5 }}>Accept: text/csv</Box> for streamed CSV
                                        downloads.
                                    </Alert>
                                    <Paper variant='outlined' sx={{ p: 2.5, mb: 3 }}>
                                        <Typography variant='h6' gutterBottom>List request parameters</Typography>
                                        <Typography color='text.secondary' paragraph>
                                            Routes ending in <Box component='code'>/l/i/s/t</Box> accept either a JSON
                                            body with <Box component='code'>Content-Type: application/json</Box> or a
                                            form body with
                                            <Box component='code' sx={{ ml: 0.5 }}>Content-Type: application/x-www-form-urlencoded</Box>.
                                            The response identifies the fields available under
                                            <Box component='code' sx={{ mx: 0.5 }}>filterable</Box> and
                                            <Box component='code'>sortable</Box>.
                                        </Typography>
                                        <Stack spacing={0.75}>
                                            <Box component='code'>column=value</Box>
                                            <Box component='code'>column[o]=contains&amp;column[v]=value</Box>
                                            <Box component='code'>column[in][]=value</Box>
                                            <Box component='code'>column[notIn][]=value</Box>
                                            <Box component='code'>column[from]=10&amp;column[to]=20</Box>
                                            <Box component='code'>sort[0][by]=column&amp;sort[0][dir]=ASC</Box>
                                            <Box component='code'>c=column1,column2&amp;perPage=10</Box>
                                        </Stack>
                                        <Typography color='text.secondary' sx={{ mt: 2 }}>
                                            Date-time filters use <Box component='code'>Y-m-dTH:i:s</Box>. Path values
                                            such as <Box component='code'>coreId</Box> and <Box component='code'>key</Box>
                                            are documented on the endpoints that require them.
                                        </Typography>
                                    </Paper>
                                    {filteredDirect.map(endpoint => (
                                        <EndpointSection key={endpoint.id} endpoint={endpoint} compact />
                                    ))}
                                </>
                            )}

                            {normalizedQuery &&
                                !filteredProcessor.length &&
                                !filteredWhitelist.length &&
                                !filteredDirect.length && (
                                    <Alert severity='info'>No documentation matches “{query}”.</Alert>
                                )}
                        </Paper>
                    </Box>
                </Container>
                ) : (
                    <Container maxWidth='xl'>
                        <Paper
                            elevation={0}
                            sx={{
                                my: { xs: 2, lg: 4 },
                                px: { xs: 2, md: 5 },
                                py: { xs: 2, md: 4 }
                            }}
                        >
                            <TransactionPlayground />
                        </Paper>
                    </Container>
                )}
            </Box>
        </Screen>
    );
};

export default Documentation;
